import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, HostListener, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ScreenshotMetadata, ScreenshotService } from '@ld.consumer-portal/data-access';

type Viewport = 'desktop' | 'mobile' | 'mobile-friendly';

export interface ScreenshotGroup {
  name: string;
  route: string;
  screenshots: ScreenshotMetadata[];
  stepOrder?: number;
}

@Component({
  selector: 'ld-screenshot-gallery',
  templateUrl: './screenshot-gallery.component.html',
  styleUrl: './screenshot-gallery.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatCardModule, MatProgressSpinnerModule, MatButtonModule, MatIconModule],
})
export class ScreenshotGalleryComponent {
  private readonly screenshotService = inject(ScreenshotService);

  readonly screenshotsByCategory = rxResource({
    stream: () => this.screenshotService.getScreenshotsByCategory(),
  });

  readonly selectedViewports = signal<Set<Viewport>>(new Set(['desktop']));
  readonly expandedScreenshot = signal<ScreenshotMetadata | null>(null);

  readonly viewportOptions: { value: Viewport; label: string }[] = [
    { value: 'desktop', label: 'Desktop' },
    { value: 'mobile', label: 'Mobile' },
  ];

  readonly allScreenshots = computed(() => {
    return this.getAllScreenshots();
  });

  readonly currentIndex = computed(() => {
    const expanded = this.expandedScreenshot();
    if (!expanded) return -1;

    const all = this.allScreenshots();
    return all.findIndex((s) => s.filename === expanded.filename);
  });

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (!this.expandedScreenshot()) return;

    if (event.key === 'ArrowLeft') {
      this.navigatePrevious();
      event.preventDefault();
    } else if (event.key === 'ArrowRight') {
      this.navigateNext();
      event.preventDefault();
    } else if (event.key === 'Escape') {
      this.closeScreenshot();
      event.preventDefault();
    }
  }

  readonly groupedScreenshots = computed(() => {
    const screenshots = this.screenshotsByCategory.value();
    const selected = this.selectedViewports();

    if (!screenshots) {
      return null;
    }

    const result = new Map<string, ScreenshotGroup[]>();

    screenshots.forEach((items, category) => {
      const groupMap = new Map<string, ScreenshotMetadata[]>();
      items.forEach((item) => {
        const existing = groupMap.get(item.name);
        if (existing) {
          existing.push(item);
        } else {
          groupMap.set(item.name, [item]);
        }
      });

      const groups: ScreenshotGroup[] = [];
      groupMap.forEach((groupItems, name) => {
        const filteredItems = groupItems.filter((item) => selected.has(item.viewport as Viewport));

        if (filteredItems.length > 0) {
          const viewportOrder = ['desktop', 'mobile-friendly', 'mobile', 'unknown'];
          filteredItems.sort((a, b) => viewportOrder.indexOf(a.viewport) - viewportOrder.indexOf(b.viewport));

          const stepOrder = filteredItems[0].stepOrder;

          groups.push({
            name,
            route: filteredItems[0].route,
            screenshots: filteredItems,
            stepOrder,
          });
        }
      });

      if (groups.length > 0) {
        const isWizardCategory = category.includes('Wizard');
        if (isWizardCategory) {
          groups.sort((a, b) => {
            const orderA = a.stepOrder ?? 999;
            const orderB = b.stepOrder ?? 999;
            return orderA - orderB;
          });
        } else {
          groups.sort((a, b) => a.name.localeCompare(b.name));
        }
        result.set(category, groups);
      }
    });

    return result;
  });

  readonly categories = computed(() => {
    const screenshots = this.groupedScreenshots();
    if (!screenshots) {
      return [];
    }
    return Array.from(screenshots.keys());
  });

  toggleViewport(viewport: Viewport): void {
    const current = this.selectedViewports();
    const newSet = new Set(current);

    if (newSet.has(viewport)) {
      if (newSet.size > 1) {
        newSet.delete(viewport);
      }
    } else {
      newSet.add(viewport);
    }

    this.selectedViewports.set(newSet);
  }

  isViewportSelected(viewport: Viewport): boolean {
    return this.selectedViewports().has(viewport);
  }

  openScreenshot(screenshot: ScreenshotMetadata): void {
    this.expandedScreenshot.set(screenshot);
  }

  closeScreenshot(): void {
    this.expandedScreenshot.set(null);
  }

  navigatePrevious(): void {
    const all = this.allScreenshots();
    const index = this.currentIndex();
    if (index > 0) {
      this.expandedScreenshot.set(all[index - 1]);
    }
  }

  navigateNext(): void {
    const all = this.allScreenshots();
    const index = this.currentIndex();
    if (index < all.length - 1) {
      this.expandedScreenshot.set(all[index + 1]);
    }
  }

  canNavigatePrevious(): boolean {
    return this.currentIndex() > 0;
  }

  canNavigateNext(): boolean {
    const all = this.allScreenshots();
    return this.currentIndex() < all.length - 1;
  }

  isWizardCategory(category: string): boolean {
    return category.includes('Wizard');
  }

  getFlatScreenshots(category: string): ScreenshotMetadata[] {
    const screenshots = this.screenshotsByCategory.value();
    const selected = this.selectedViewports();

    if (!screenshots) return [];

    const items = screenshots.get(category) || [];

    const filtered = items.filter((item) => selected.has(item.viewport as Viewport));

    const viewportOrder = ['desktop', 'mobile-friendly', 'mobile', 'unknown'];
    return filtered.sort((a, b) => {
      const nameCompare = a.name.localeCompare(b.name);
      if (nameCompare !== 0) return nameCompare;
      return viewportOrder.indexOf(a.viewport) - viewportOrder.indexOf(b.viewport);
    });
  }

  getAllScreenshots(): ScreenshotMetadata[] {
    return [
      ...this.getNonWizardScreenshots(),
      ...this.getJvScreenshots(),
      ...this.getPurchaseWizardScreenshots(),
      ...this.getRefinanceWizardScreenshots(),
    ];
  }

  isJvCategory(category: string): boolean {
    return category === 'Joint Ventures';
  }

  getJvBrandScreenshots(): ScreenshotMetadata[] {
    const screenshots = this.screenshotsByCategory.value();
    const selected = this.selectedViewports();

    if (!screenshots) return [];

    const items = screenshots.get('Joint Ventures') || [];
    const filtered = items.filter((item) => selected.has(item.viewport as Viewport));

    const viewportOrder = ['desktop', 'mobile-friendly', 'mobile', 'unknown'];
    return filtered.sort((a, b) => {
      const nameCompare = a.name.localeCompare(b.name);
      if (nameCompare !== 0) return nameCompare;
      return viewportOrder.indexOf(a.viewport) - viewportOrder.indexOf(b.viewport);
    });
  }

  getJvScreenshots(): ScreenshotMetadata[] {
    const screenshots = this.screenshotsByCategory.value();
    const selected = this.selectedViewports();

    if (!screenshots) return [];

    const result: ScreenshotMetadata[] = [];
    screenshots.forEach((items, category) => {
      if (this.isJvCategory(category)) {
        const filtered = items.filter((item) => selected.has(item.viewport as Viewport));
        result.push(...filtered);
      }
    });

    return result.sort((a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name));
  }

  getNonWizardScreenshots(): ScreenshotMetadata[] {
    const screenshots = this.screenshotsByCategory.value();
    const selected = this.selectedViewports();

    if (!screenshots) return [];

    const result: ScreenshotMetadata[] = [];
    screenshots.forEach((items, category) => {
      if (!category.includes('Wizard') && !this.isJvCategory(category)) {
        const filtered = items.filter((item) => selected.has(item.viewport as Viewport));
        result.push(...filtered);
      }
    });

    const viewportOrder = ['desktop', 'mobile-friendly', 'mobile', 'unknown'];
    return result.sort((a, b) => {
      const nameCompare = a.name.localeCompare(b.name);
      if (nameCompare !== 0) return nameCompare;
      return viewportOrder.indexOf(a.viewport) - viewportOrder.indexOf(b.viewport);
    });
  }

  getPurchaseWizardScreenshots(): ScreenshotMetadata[] {
    const screenshots = this.screenshotsByCategory.value();
    const selected = this.selectedViewports();

    if (!screenshots) return [];

    const result: ScreenshotMetadata[] = [];
    screenshots.forEach((items, category) => {
      if (category.includes('Purchase') && category.includes('Wizard')) {
        const filtered = items.filter((item) => selected.has(item.viewport as Viewport));
        result.push(...filtered);
      }
    });

    const viewportOrder = ['desktop', 'mobile-friendly', 'mobile', 'unknown'];
    return result.sort((a, b) => {
      const stepA = a.stepOrder ?? 999;
      const stepB = b.stepOrder ?? 999;
      if (stepA !== stepB) return stepA - stepB;
      return viewportOrder.indexOf(a.viewport) - viewportOrder.indexOf(b.viewport);
    });
  }

  getRefinanceWizardScreenshots(): ScreenshotMetadata[] {
    const screenshots = this.screenshotsByCategory.value();
    const selected = this.selectedViewports();

    if (!screenshots) return [];

    const result: ScreenshotMetadata[] = [];
    screenshots.forEach((items, category) => {
      if (category.includes('Refinance') && category.includes('Wizard')) {
        const filtered = items.filter((item) => selected.has(item.viewport as Viewport));
        result.push(...filtered);
      }
    });

    const viewportOrder = ['desktop', 'mobile-friendly', 'mobile', 'unknown'];
    return result.sort((a, b) => {
      const stepA = a.stepOrder ?? 999;
      const stepB = b.stepOrder ?? 999;
      if (stepA !== stepB) return stepA - stepB;
      return viewportOrder.indexOf(a.viewport) - viewportOrder.indexOf(b.viewport);
    });
  }
}
